/**
 * TavernPanel -- Party management at the tavern.
 *
 * Shows current party, available recruits (with NPC rarity),
 * bench/activate, hire, and dismiss functionality.
 *
 * @module TavernPanel
 */

import focusManager from '../../input/FocusManager.js';
import townSystem, { NPC_HIRE_COSTS } from '../../systems/TownSystem.js';

/** NPC rarity display colors. */
const NPC_RARITY_COLORS = {
  common: '#cccccc',
  uncommon: '#44cc44',
  rare: '#4488ff',
  legendary: '#ff8800',
};

/** NPC rarity display names. */
const NPC_RARITY_NAMES = {
  common: 'Common',
  uncommon: 'Uncommon',
  rare: 'Rare',
  legendary: 'Legendary',
};

export default class TavernPanel {
  constructor(options = {}) {
    this._party = options.party || null;
    this._townId = options.townId || null;
    this._onClose = options.onClose || null;
    this._el = null;
    this._isOpen = false;
    this._statusMsg = '';
    this._tab = 'party'; // 'party', 'recruits', 'bench'
  }

  get isOpen() { return this._isOpen; }

  open() {
    if (this._isOpen) return;
    const uiRoot = document.getElementById('ui-root');
    if (!uiRoot) return;

    this.close();
    this._isOpen = true;

    this._el = document.createElement('div');
    this._el.className = 'town-panel-overlay';
    this._el.id = 'tavern-panel';
    this._el.setAttribute('role', 'dialog');
    this._el.setAttribute('aria-label', 'Tavern');
    this._el.setAttribute('data-scene', 'tavern');

    this._render();
    uiRoot.appendChild(this._el);
    focusManager.pushGroup('tavern', this._el);
  }

  close() {
    if (this._el && this._el.parentNode) {
      this._el.parentNode.removeChild(this._el);
      const topGroup = focusManager._currentGroup ? focusManager._currentGroup() : null;
      if (topGroup && topGroup.name === 'tavern') {
        focusManager.popGroup();
      }
    }
    this._el = null;
    this._isOpen = false;
    this._statusMsg = '';
    if (this._onClose) this._onClose();
  }

  _render() {
    if (!this._el) return;
    this._el.innerHTML = '';

    const panel = document.createElement('div');
    panel.className = 'town-panel';

    // Header
    const header = document.createElement('div');
    header.className = 'town-panel-header';
    const title = document.createElement('h2');
    title.className = 'town-panel-title';
    title.textContent = 'Tavern';
    header.appendChild(title);

    const gold = document.createElement('span');
    gold.className = 'town-panel-gold';
    gold.textContent = `Gold: ${this._party?.gold || 0}`;
    header.appendChild(gold);

    const closeBtn = document.createElement('button');
    closeBtn.className = 'town-panel-close';
    closeBtn.setAttribute('data-focusable', 'true');
    closeBtn.textContent = 'X';
    closeBtn.addEventListener('click', () => this.close());
    header.appendChild(closeBtn);
    panel.appendChild(header);

    // Tabs
    const tabs = document.createElement('div');
    tabs.className = 'town-panel-tabs';

    for (const [key, label] of [['party', 'Current Party'], ['recruits', 'Available Recruits'], ['bench', 'Bench']]) {
      const tab = document.createElement('button');
      tab.className = `town-panel-tab${this._tab === key ? ' town-panel-tab--active' : ''}`;
      tab.setAttribute('data-focusable', 'true');
      tab.textContent = label;
      tab.addEventListener('click', () => { this._tab = key; this._render(); focusManager.refreshGroup(); });
      tabs.appendChild(tab);
    }
    panel.appendChild(tabs);

    // Status
    if (this._statusMsg) {
      const status = document.createElement('div');
      status.className = 'town-panel-status';
      status.textContent = this._statusMsg;
      panel.appendChild(status);
    }

    const content = document.createElement('div');
    content.className = 'town-panel-content';

    switch (this._tab) {
      case 'party':
        this._renderPartyTab(content);
        break;
      case 'recruits':
        this._renderRecruitsTab(content);
        break;
      case 'bench':
        this._renderBenchTab(content);
        break;
    }

    panel.appendChild(content);
    this._el.appendChild(panel);
  }

  _renderPartyTab(container) {
    const party = this._party;
    if (!party || party.members.length === 0) {
      const empty = document.createElement('div');
      empty.className = 'town-panel-empty';
      empty.textContent = 'No party members.';
      container.appendChild(empty);
      return;
    }

    const partyInfo = document.createElement('div');
    partyInfo.className = 'town-panel-desc';
    partyInfo.textContent = `Party: ${party.members.length}/4 members`;
    container.appendChild(partyInfo);

    const grid = document.createElement('div');
    grid.className = 'town-item-grid';

    for (let i = 0; i < party.members.length; i++) {
      const member = party.members[i];

      const card = document.createElement('div');
      card.className = 'town-tavern-card';

      const name = document.createElement('div');
      name.className = 'town-tavern-name';
      name.textContent = member.name;
      card.appendChild(name);

      const info = document.createElement('div');
      info.className = 'town-tavern-info';
      info.textContent = `Lvl ${member.level} ${member.getClassName?.()} | ${member.getRace?.()?.name || member.raceId}`;
      card.appendChild(info);

      const hp = document.createElement('div');
      hp.className = 'town-tavern-hp';
      hp.textContent = `HP: ${member.hp}/${member.maxHp} | Mana: ${member.mana}/${member.maxMana}`;
      card.appendChild(hp);

      // Actions
      const actions = document.createElement('div');
      actions.className = 'town-tavern-actions';

      if (party.members.length > 1) {
        const benchBtn = document.createElement('button');
        benchBtn.className = 'town-panel-btn town-panel-btn--sm';
        benchBtn.setAttribute('data-focusable', 'true');
        benchBtn.textContent = 'Bench';
        benchBtn.addEventListener('click', () => {
          const result = townSystem.benchMember(party, i);
          this._statusMsg = result.message;
          this._render();
          focusManager.refreshGroup();
        });
        actions.appendChild(benchBtn);

        const dismissBtn = document.createElement('button');
        dismissBtn.className = 'town-panel-btn town-panel-btn--sm town-panel-btn--danger';
        dismissBtn.setAttribute('data-focusable', 'true');
        dismissBtn.textContent = 'Dismiss';
        dismissBtn.addEventListener('click', () => {
          const result = townSystem.dismissMember(party, i);
          this._statusMsg = result.message;
          this._render();
          focusManager.refreshGroup();
        });
        actions.appendChild(dismissBtn);
      }

      card.appendChild(actions);
      grid.appendChild(card);
    }

    container.appendChild(grid);
  }

  _renderRecruitsTab(container) {
    const party = this._party;
    const level = party?.partyLevel || 1;
    const recruits = townSystem.getTavernRecruits(this._townId, level);

    if (recruits.length === 0) {
      const empty = document.createElement('div');
      empty.className = 'town-panel-empty';
      empty.textContent = 'No recruits available.';
      container.appendChild(empty);
      return;
    }

    const grid = document.createElement('div');
    grid.className = 'town-item-grid';

    for (let i = 0; i < recruits.length; i++) {
      const recruit = recruits[i];
      const canAfford = (party?.gold || 0) >= recruit.hireCost;
      const partyFull = (party?.members.length || 0) >= 4;

      const card = document.createElement('div');
      card.className = `town-tavern-card${(!canAfford || partyFull) ? ' town-item-card--disabled' : ''}`;
      card.setAttribute('data-focusable', 'true');

      // Rarity badge
      const badge = document.createElement('span');
      badge.className = 'town-tavern-rarity';
      badge.style.color = NPC_RARITY_COLORS[recruit.npcRarity] || NPC_RARITY_COLORS.common;
      badge.textContent = NPC_RARITY_NAMES[recruit.npcRarity] || 'Common';
      card.appendChild(badge);

      const name = document.createElement('div');
      name.className = 'town-tavern-name';
      name.textContent = recruit.name;
      card.appendChild(name);

      const info = document.createElement('div');
      info.className = 'town-tavern-info';
      info.textContent = `Lvl ${recruit.level} ${recruit.className} | ${recruit.raceName}`;
      card.appendChild(info);

      // Attribute summary
      const attrs = document.createElement('div');
      attrs.className = 'town-tavern-attrs';
      const attrParts = Object.entries(recruit.attributes).map(([k, v]) => `${k.toUpperCase()}: ${v}`);
      attrs.textContent = attrParts.join(' | ');
      card.appendChild(attrs);

      const price = document.createElement('div');
      price.className = 'town-item-price';
      price.textContent = `Hire: ${recruit.hireCost}g`;
      card.appendChild(price);

      if (canAfford && !partyFull) {
        card.addEventListener('click', () => {
          const result = townSystem.hireRecruit(party, this._townId, i);
          this._statusMsg = result.message;
          this._render();
          focusManager.refreshGroup();
        });
      }

      grid.appendChild(card);
    }

    container.appendChild(grid);
  }

  _renderBenchTab(container) {
    const bench = townSystem.getBench();
    const partyFull = (this._party?.members.length || 0) >= 4;

    if (bench.length === 0) {
      const empty = document.createElement('div');
      empty.className = 'town-panel-empty';
      empty.textContent = 'No benched members.';
      container.appendChild(empty);
      return;
    }

    const grid = document.createElement('div');
    grid.className = 'town-item-grid';

    for (let i = 0; i < bench.length; i++) {
      const member = bench[i];

      const card = document.createElement('div');
      card.className = `town-tavern-card${partyFull ? ' town-item-card--disabled' : ''}`;
      card.setAttribute('data-focusable', 'true');

      const name = document.createElement('div');
      name.className = 'town-tavern-name';
      name.textContent = member.name;
      card.appendChild(name);

      const info = document.createElement('div');
      info.className = 'town-tavern-info';
      info.textContent = `Lvl ${member.level} ${member.getClassName?.()} | ${member.getRace?.()?.name || member.raceId}`;
      card.appendChild(info);

      if (!partyFull) {
        const activateBtn = document.createElement('button');
        activateBtn.className = 'town-panel-btn town-panel-btn--sm';
        activateBtn.setAttribute('data-focusable', 'true');
        activateBtn.textContent = 'Activate';
        activateBtn.addEventListener('click', () => {
          const result = townSystem.activateMember(this._party, i);
          this._statusMsg = result.message;
          this._render();
          focusManager.refreshGroup();
        });
        card.appendChild(activateBtn);
      }

      grid.appendChild(card);
    }

    container.appendChild(grid);
  }
}
